import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Loader2, X } from "lucide-react";

const STATE_KEY = "quanta.pending_tenant_pick";

/**
 * Phase 22 — Tenant picker. Landed here from ``/login`` when the
 * identity has multiple memberships. Pre-selects the identity's
 * ``last_tenant_id`` after a 3-second delay unless the user
 * cancels/clicks another card.
 */
export default function SelectTenantPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { selectTenant } = useAuth();
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [cancelled, setCancelled] = useState(false);
  const timerRef = useRef(null);

  const state = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(STATE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return null;
  }, []);

  useEffect(() => {
    if (!state) {
      toast.error("Your sign-in expired. Please sign in again.");
      navigate("/login", { replace: true });
    }
  }, [state, navigate]);

  const pick = async (tenant_id) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setBusy(true);
    try {
      const data = await selectTenant({ temp_token: state.temp_token, tenant_id });
      sessionStorage.removeItem(STATE_KEY);
      toast.success(`Signed in to ${(state.memberships.find((m) => m.tenant_id === tenant_id) || {}).tenant_name}`);
      navigate("/app/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!state || cancelled) return undefined;
    const def = state.default_tenant_id;
    if (!def || !state.memberships.find((m) => m.tenant_id === def)) return undefined;
    let n = 3;
    setCountdown(n);
    timerRef.current = setInterval(() => {
      n -= 1;
      setCountdown(n);
      if (n <= 0) {
        clearInterval(timerRef.current);
        pick(def);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [state, cancelled]);

  if (!state) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="select-tenant-page">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="mb-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Choose your workspace
        </div>
        <h1 className="font-display text-3xl font-semibold text-primary">
          Where would you like to sign in?
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          You're a member of {state.memberships.length} workspaces. Pick one
          — you can switch anytime from the top nav.
        </p>

        {state.default_tenant_id && !cancelled && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-secondary/30 bg-secondary/5 px-4 py-3 text-sm">
            <span className="text-secondary">
              Auto-selecting your most recent workspace in <b>{countdown}s</b>…
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCancelled(true);
                if (timerRef.current) clearInterval(timerRef.current);
              }}
              data-testid="select-tenant-cancel-auto"
            >
              <X className="mr-1 h-3.5 w-3.5" /> Cancel
            </Button>
          </div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="select-tenant-cards">
          {state.memberships.map((m) => (
            <Card
              key={m.tenant_id}
              className={busy ? "opacity-60" : "cursor-pointer transition hover:border-secondary"}
              onClick={() => !busy && pick(m.tenant_id)}
              data-testid={`select-tenant-card-${m.tenant_slug}`}
            >
              <CardContent className="flex items-center gap-3 p-4">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-secondary/10 text-secondary">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate font-semibold text-primary">{m.tenant_name}</div>
                    {m.tenant_id === state.default_tenant_id && (
                      <Badge variant="secondary" className="text-[10px]">last used</Badge>
                    )}
                  </div>
                  <div className="mt-1 truncate text-xs text-muted-foreground">
                    {(m.roles || []).join(", ")}
                  </div>
                </div>
                {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export function stashPendingSelection(data) {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      temp_token: data.temp_token,
      memberships: data.memberships,
      default_tenant_id: data.default_tenant_id,
      identity: data.identity,
    }));
  } catch (_) {}
}
